import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { currentSelectedLanguage } from "../../../../../StateManagement/actions";
import { contactsService } from "../../../../../Services/contactsService";
import { ContactModal } from "./ContactModal";
import { ContactGroupModal } from "./ContactGroupModal";
import { ImportContactsModal } from "./ImportContactsModal";
import { ImportGroupsModal } from "./ImportGroupsModal";
import { ContactsList } from "./ContactsList";
import { ContactGroupsList } from "./ContactGroupsList";
import ValidationPopup from "../../../History/Modals/ValidationPopup";
import Alert from "../../../../Controls/Alerts/Alerts";
import LoadingSpinner from "../../../../Controls/Spinner/LoadingSpinner";
import "../../../../Shared/CommonTab/common-tabs.css";
import "./Contacts.css";

export function ContactsContainer({ searchedCompany = "", searchedUser = "" }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const settingFor = useSelector((state) => state.settings?.settingFor);
  const userRole = useMemo(() => localStorage.getItem("UserRole") || "", []);
  const isAdmin = userRole === "RDocsCompanyAdmin" || userRole === "RDocsSupportAdmin" || userRole === "Admin";
  const loggedInUser = (localStorage.getItem("UserName") || localStorage.getItem("userEmail") || "").toLowerCase().trim();

  // Effective scope: Company or Personal
  const currentScope = (settingFor === "Company") ? "Company" : "Personal";

  // Determine effective company name
  const effectiveCompany = useMemo(() => {
    if (userRole === "RDocsSupportAdmin") {
      return searchedCompany;
    }
    return localStorage.getItem("Companyname") || "";
  }, [userRole, searchedCompany]);

  // Regular user in Company scope is read-only
  const isReadOnlyScope = userRole === "RDocsUser" && currentScope === "Company";

  // Active view: 'contacts' | 'groups'
  const [activeTab, setActiveTab] = useState("contacts");
  const [searchQuery, setSearchQuery] = useState("");

  // Checkbox Selection States
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());

  // Backend data
  const [contacts, setContacts] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modal dialog states
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportGroupsModalOpen, setIsImportGroupsModalOpen] = useState(false);

  // Validation Popup state for file mistakes
  const [isValidationPopup, setIsValidationPopup] = useState(false);
  const [validationData, setValidationData] = useState({});

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Clear selections when changing tabs, filtering, scope, or searched user/company
  useEffect(() => {
    setSelectedContactIds(new Set());
    setSelectedGroupIds(new Set());
  }, [activeTab, searchQuery, searchedUser, effectiveCompany, currentScope]);

  // Alert State
  const [alertMsg, setAlertMsg] = useState("");
  const [alertType, setAlertType] = useState("");

  const showAlert = (message, type = "success") => {
    setAlertMsg("");
    setTimeout(() => {
      setAlertMsg(message);
      setAlertType(type);
    }, 10);
  };

  // Load contacts and groups from backend API
  const loadData = useCallback(async () => {
    if (userRole === "RDocsSupportAdmin" && currentScope === "Company" && !effectiveCompany && !searchedUser) {
      setContacts([]);
      setContactGroups([]);
      return;
    }

    setIsLoading(true);
    try {
      const [fetchedContacts, fetchedGroups] = await Promise.all([
        contactsService.getContacts(currentScope, searchedUser, "", effectiveCompany),
        contactsService.getContactGroups(currentScope, searchedUser, "", effectiveCompany)
      ]);

      const parseArray = (res, keys = []) => {
        if (Array.isArray(res)) return res;
        if (res && typeof res === "object") {
          for (const k of keys) {
            if (Array.isArray(res[k])) return res[k];
          }
        }
        return [];
      };

      const parsedContacts = parseArray(fetchedContacts, ["contacts", "data", "lstContacts"]);
      const parsedGroups = parseArray(fetchedGroups, ["contactGroups", "groups", "data", "lstContactGroups", "lstGroups"]);

      setContacts(parsedContacts);
      setContactGroups(parsedGroups);
    } catch (err) {
      console.error("Error fetching contacts/groups:", err);
      showAlert(err.message || "Failed to load directory data", "warning");
    } finally {
      setIsLoading(false);
    }
  }, [currentScope, searchedUser, effectiveCompany, userRole]);

  useEffect(() => {
    setSelectedContactIds(new Set());
    setSelectedGroupIds(new Set());
    setContacts([]);
    setContactGroups([]);
    loadData();
  }, [currentScope, loadData]);

  // Suggested Domains management
  const derivedDomains = useMemo(() => {
    const set = new Set();
    contacts.forEach((c) => {
      if (!c.isHiddenFromDirectory && !c.isGroupOnly && c.email && c.email.includes("@")) {
        const domain = c.email.split("@")[1]?.toLowerCase().trim();
        if (domain && domain.includes(".")) set.add(domain);
      }
    });
    return Array.from(set);
  }, [contacts]);

  const [customSuggestedDomains, setCustomSuggestedDomains] = useState([]);

  const allSuggestedDomains = useMemo(() => {
    return Array.from(new Set([...derivedDomains, ...customSuggestedDomains]));
  }, [derivedDomains, customSuggestedDomains]);

  const handleDomainsImport = (newDomains) => {
    setCustomSuggestedDomains((prev) => Array.from(new Set([...prev, ...newDomains.map((d) => d.toLowerCase().trim())])));
    showAlert(`${selectedLanguage.LstLanguageCodes.UI_YouAdded} ${newDomains.length} ${selectedLanguage.LstLanguageCodes.UI_DomainsToSuggestedDomains}`, "success");
  };

  // Filtered & Alphabetically Sorted Contacts by Email (A-Z)
  const filteredContacts = useMemo(() => {
    let list = contacts.filter((c) => !c.isHiddenFromDirectory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((c) => {
        const fullName = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
        const email = (c.email || "").toLowerCase();
        const mobile = (c.mobile || "").toLowerCase();
        const ref = (c.referenceTag || "").toLowerCase();
        return (fullName.includes(q) || email.includes(q) || mobile.includes(q) || ref.includes(q));
      });
    }
    return [...list].sort((a, b) => {
      const emailA = (a.email || "").toLowerCase();
      const emailB = (b.email || "").toLowerCase();
      return emailA.localeCompare(emailB);
    });
  }, [contacts, searchQuery]);

  // Filtered & Alphabetically Sorted Groups by Name (A-Z)
  const filteredGroups = useMemo(() => {
    let list = contactGroups;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = contactGroups.filter((g) => {
        const name = (g.name || "").toLowerCase();
        const desc = (g.description || "").toLowerCase();
        const alias = (g.aliasEmail || "").toLowerCase();
        return name.includes(q) || desc.includes(q) || alias.includes(q);
      });
    }
    return [...list].sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [contactGroups, searchQuery]);

  const selectableContacts = useMemo(() => {
    return filteredContacts.filter((c) => {
      const createdByEmail = (c.createdBy || c.createdUser || "").toLowerCase().trim();
      const isCreator = Boolean(loggedInUser && createdByEmail && (loggedInUser === createdByEmail || createdByEmail.includes(loggedInUser)));
      const isReadOnly = (Boolean(c.isReadOnly) || isReadOnlyScope) && !isCreator && !isAdmin;
      return !isReadOnly;
    });
  }, [filteredContacts, loggedInUser, isReadOnlyScope, isAdmin]);

  const selectableGroups = useMemo(() => {
    return filteredGroups.filter((g) => {
      const createdByEmail = (g.createdBy || g.createdUser || "").toLowerCase().trim();
      const isCreator = Boolean(loggedInUser && createdByEmail && (loggedInUser === createdByEmail || createdByEmail.includes(loggedInUser)));
      const isReadOnly = (Boolean(g.isReadOnly) || isReadOnlyScope) && !isCreator && !isAdmin;
      return !isReadOnly;
    });
  }, [filteredGroups, loggedInUser, isReadOnlyScope, isAdmin]);

  // Contact Handlers
  const handleOpenAddContact = () => {
    setEditingContact(null);
    setIsContactModalOpen(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setIsContactModalOpen(true);
  };

  const handleSaveContact = async (contactData) => {
    try {
      setIsLoading(true);
      const currentUser = (localStorage.getItem("UserName") || localStorage.getItem("userEmail") || "").trim();
      const effectiveCreatedBy = searchedUser ? searchedUser : currentUser;
      const targetId = contactData.id || editingContact?.id || editingContact?._id || editingContact?.contactId;
      const payload = {
        ...(editingContact || {}),
        ...contactData,
        id: targetId,
        scope: contactData.scope || editingContact?.scope || currentScope,
        companyName: editingContact?.companyName || effectiveCompany,
        createdBy: editingContact?.createdBy || contactData.createdBy || effectiveCreatedBy
      };

      if (editingContact && targetId) {
        const res = await contactsService.updateContact(targetId, payload, payload.scope, payload.companyName);
        const updatedObj = res?.contact || res?.data || (res && (res.id || res._id) ? res : payload);
        setContacts((prev) => prev.map((c) => (c.id === targetId || c.email === contactData.email ? { ...c, ...updatedObj } : c)));
        showAlert(selectedLanguage.LstLanguageCodes.UI_ContactUpdatedSuccess, "success");
      } else {
        const res = await contactsService.createContact(payload, payload.scope, payload.companyName);
        const createdObj = res?.contact || res?.data || (res && (res.id || res.email) ? res : { ...payload, id: res?.id || `contact-${Date.now()}` });
        setContacts((prev) => [...prev.filter((c) => c.id !== createdObj.id && c.email !== createdObj.email), createdObj]);
        showAlert(selectedLanguage.LstLanguageCodes.UI_ContactCreatedSuccess, "success");
      }
      setIsContactModalOpen(false);
      setEditingContact(null);
      await loadData();
    } catch (err) {
      showAlert(err.message || "Failed to save contact", "warning");
    } finally {
      setIsLoading(false);
    }
  };

  // Group Handlers
  const handleOpenAddGroup = () => {
    setEditingGroup(null);
    setIsGroupModalOpen(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setIsGroupModalOpen(true);
  };

  const handleSaveGroup = async (groupData) => {
    try {
      setIsLoading(true);
      const currentUser = (localStorage.getItem("UserName") || localStorage.getItem("userEmail") || "").trim();
      const effectiveCreatedBy = searchedUser ? searchedUser : currentUser;
      const targetId = groupData.id || editingGroup?.id || editingGroup?._id || editingGroup?.groupId;
      const payload = {
        ...(editingGroup || {}),
        ...groupData,
        id: targetId,
        scope: groupData.scope || editingGroup?.scope || currentScope,
        companyName: editingGroup?.companyName || effectiveCompany,
        createdBy: editingGroup?.createdBy || groupData.createdBy || effectiveCreatedBy
      };

      if (editingGroup && targetId) {
        const res = await contactsService.updateContactGroup(targetId, payload, payload.scope, payload.companyName);
        const updatedObj = res?.group || res?.data || (res && (res.id || res._id) ? res : payload);
        setContactGroups((prev) => prev.map((g) => (g.id === targetId || g.name === groupData.name ? { ...g, ...updatedObj, ...groupData } : g)));
        showAlert(`"${groupData.name}" ${selectedLanguage.LstLanguageCodes.UI_UpdateSuccess}.`, "success");
      } else {
        const res = await contactsService.createContactGroup(payload, payload.scope, payload.companyName);
        const createdObj = res?.group || res?.data || (res && (res.id || res.name) ? res : { ...payload, id: res?.id || `group-${Date.now()}` });
        setContactGroups((prev) => [...prev.filter((g) => g.id !== createdObj.id && g.name !== createdObj.name), createdObj]);
        showAlert(`"${groupData.name}" ${selectedLanguage.LstLanguageCodes.UI_GroupCreatedSuccess}`, "success");
      }
      setIsGroupModalOpen(false);
      setEditingGroup(null);
      await loadData();
    } catch (err) {
      showAlert(err.message || "Failed to save group", "warning");
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk Import Handler for Contacts Tab
  const handleImportContacts = async (newContacts, duplicateFound = false) => {
    try {
      setIsLoading(true);
      let resCount = 0;
      if (newContacts && newContacts.length > 0) {
        const res = await contactsService.importContacts(newContacts, currentScope, effectiveCompany);
        resCount = res?.importedCount || newContacts.length;
      }
      if (duplicateFound) {
        showAlert(selectedLanguage?.LstLanguageCodes?.UI_DeDuplicateMsg, "warning");
      } else if (resCount > 0) {
        showAlert(`Successfully imported ${resCount} contact(s).`, "success");
      }
      await loadData();
    } catch (err) {
      showAlert(err.message || "Failed to import contacts", "warning");
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk Import Handler for Groups Tab
  const handleImportGroups = async (newGroups, duplicateFound = false) => {
    try {
      setIsLoading(true);
      let importedCount = 0;
      if (newGroups && newGroups.length > 0) {
        await Promise.all(newGroups.map((g) => contactsService.createContactGroup({ ...g, scope: currentScope, companyName: effectiveCompany })));
        importedCount = newGroups.length;
      }
      if (duplicateFound) {
        showAlert(selectedLanguage?.LstLanguageCodes?.UI_DuplicateGroupsRemoved, "warning");
      } else if (importedCount > 0) {
        showAlert(`Successfully imported ${importedCount} group(s).`, "success");
      }
      await loadData();
    } catch (err) {
      showAlert(err.message || "Failed to import groups", "warning");
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactsImportFromGroup = async (newContacts, duplicateFound = false) => {
    try {
      setIsLoading(true);
      if (newContacts && newContacts.length > 0) {
        await contactsService.importContacts(newContacts, currentScope, effectiveCompany);
      }
      if (duplicateFound) {
        showAlert(selectedLanguage?.LstLanguageCodes?.UI_DeDuplicateMsg, "warning");
      }
      await loadData();
    } catch (err) {
      console.error("Error saving contacts from group:", err);
      showAlert(err.message || "Failed to save contacts", "warning");
    } finally {
      setIsLoading(false);
    }
  };

  // Checkbox Selection Handlers
  const handleToggleSelectContact = (id) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllContacts = () => {
    if (selectableContacts.length > 0 && selectedContactIds.size === selectableContacts.length) {
      setSelectedContactIds(new Set());
    } else {
      setSelectedContactIds(new Set(selectableContacts.map((c) => c.id || c.email)));
    }
  };

  const handleToggleSelectGroup = (id) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllGroups = () => {
    if (selectableGroups.length > 0 && selectedGroupIds.size === selectableGroups.length) {
      setSelectedGroupIds(new Set());
    } else {
      setSelectedGroupIds(new Set(selectableGroups.map((g) => g.id || g.name)));
    }
  };

  // Deletion Handling
  const handleRequestDeleteContact = (contact) => {
    setDeleteConfirm({ type: "contact", item: contact });
  };

  const handleRequestDeleteGroup = (group) => {
    setDeleteConfirm({ type: "group", item: group });
  };

  const handleRequestBatchDelete = () => {
    if (activeTab === "contacts" && selectedContactIds.size > 0) {
      setDeleteConfirm({ type: "batchContacts", count: selectedContactIds.size, ids: Array.from(selectedContactIds) });
    } else if (activeTab === "groups" && selectedGroupIds.size > 0) {
      setDeleteConfirm({ type: "batchGroups", count: selectedGroupIds.size, ids: Array.from(selectedGroupIds) });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    setSelectedContactIds(new Set());
    setSelectedGroupIds(new Set());
    try {
      setIsLoading(true);
      if (deleteConfirm.type === "contact") {
        const targetId = deleteConfirm.item.id || deleteConfirm.item._id;
        const targetEmail = deleteConfirm.item.email;
        setContacts((prev) => prev.filter((c) => (c.id || c._id) !== targetId && c.email !== targetEmail));
        await contactsService.deleteContact(targetId, currentScope, effectiveCompany);
        showAlert(selectedLanguage.LstLanguageCodes.UI_ContactDeletedSuccess, "success");
      } else if (deleteConfirm.type === "group") {
        const targetId = deleteConfirm.item.id || deleteConfirm.item._id;
        const targetName = deleteConfirm.item.name;
        setContactGroups((prev) => prev.filter((g) => (g.id || g._id) !== targetId && g.name !== targetName));
        await contactsService.deleteContactGroup(targetId, currentScope, effectiveCompany);
        showAlert(selectedLanguage.LstLanguageCodes.UI_GroupDeletedSuccess, "success");
      } else if (deleteConfirm.type === "batchContacts") {
        const idsToDelete = new Set(deleteConfirm.ids);
        setContacts((prev) => prev.filter((c) => !idsToDelete.has(c.id) && !idsToDelete.has(c._id) && !idsToDelete.has(c.email)));
        await Promise.all(deleteConfirm.ids.map((id) => contactsService.deleteContact(id, currentScope, effectiveCompany)));
        showAlert(selectedLanguage.LstLanguageCodes.UI_ContactDeletedSuccess, "success");
        setSelectedContactIds(new Set());
      } else if (deleteConfirm.type === "batchGroups") {
        const idsToDelete = new Set(deleteConfirm.ids);
        setContactGroups((prev) => prev.filter((g) => !idsToDelete.has(g.id) && !idsToDelete.has(g._id) && !idsToDelete.has(g.name)));
        await Promise.all(deleteConfirm.ids.map((id) => contactsService.deleteContactGroup(id, currentScope, effectiveCompany)));
        showAlert(selectedLanguage.LstLanguageCodes.UI_GroupDeletedSuccess, "success");
        setSelectedGroupIds(new Set());
      }
      setDeleteConfirm(null);
      await loadData();
    } catch (err) {
      showAlert(err.message || "Failed to delete item(s)", "warning");
      await loadData();
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="contacts-app-root">
      {isLoading && <LoadingSpinner />}

      {alertMsg && (
        <div className="mb-3">
          <Alert alertType={alertType} alertMsg={alertMsg} closeAlert={() => setAlertMsg("")} />
        </div>
      )}

      {/* Top Header Controls */}
      <div className="contacts-main-header">
        <div className="contacts-header-left">
          {/* Sub-tabs: [ Contacts | Groups ] */}
          <ul className="nav nav-tabs common-tabs contacts-nav-tabs" id="contactsTab" role="tablist">
            <li className="nav-item" role="presentation">
              <a id="contacts-tab-link" className={`nav-link d-flex align-items-center manage-link admintab ${activeTab === "contacts" ? "active" : ""}`}
                href="#contacts" role="tab" onClick={(e) => { e.preventDefault(); setActiveTab("contacts"); }}>
                <span className="material-icons me-1 font-20">contacts</span>
                {selectedLanguage?.LstLanguageCodes?.UI_Contacts}
              </a>
            </li>
            <li className="nav-item" role="presentation">
              <a id="groups-tab-link" className={`nav-link d-flex align-items-center manage-link admintab ${activeTab === "groups" ? "active" : ""}`} href="#groups" role="tab"
                onClick={(e) => { e.preventDefault(); setActiveTab("groups"); }}>
                <span className="material-icons me-1 font-20">groups</span>
                {selectedLanguage?.LstLanguageCodes?.UI_Groups || "Groups"}
              </a>
            </li>
          </ul>

          <div className="contacts-title-area">
            <h2 className="contacts-heading-title">
              {selectedLanguage?.LstLanguageCodes?.UI_ContactsDirectory}
            </h2>
          </div>
        </div>

        <div className="contacts-header-right">
          {/* Search Box */}
          <div className="contacts-search-box-wrapper">
            {activeTab === "contacts" ? (<input type="search" className="search-Reader-FirstNameLastName mr-12p" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value?.trim()); }}
              placeholder={selectedLanguage.LstLanguageCodes.UI_EnterEmailAddressOrName} title={selectedLanguage.LstLanguageCodes.UI_EnterEmailAddressOrName}
            />) : (<input type="search" className="search-Reader-FirstNameLastName mr-12p" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value?.trim()); }}
              placeholder={selectedLanguage.LstLanguageCodes.UI_EnterGroupNameOrDLEmailGroupEmail} title={selectedLanguage.LstLanguageCodes.UI_EnterGroupNameOrDLEmailGroupEmail}
            />)}
          </div>

          {/* Action Buttons - Import available on both Contacts & Groups tab */}
          <div className="contacts-action-btns-wrapper">
            {!isReadOnlyScope && (
              <button type="button"
                onClick={() => {
                  if (activeTab === "contacts") {
                    setIsImportModalOpen(true);
                  } else {
                    setIsImportGroupsModalOpen(true);
                  }
                }}
                className="btn btn-outline-secondary border-r4 contacts-btn-secondary"
              >
                <span className="material-icons font-18">upload_file</span>
                <span>{selectedLanguage?.LstLanguageCodes?.UI_Import}</span>
              </button>
            )}

            {!isReadOnlyScope && (
              <button type="button" onClick={activeTab === "contacts" ? handleOpenAddContact : handleOpenAddGroup} className="btn sendsubmit border-r4 contacts-btn-primary">
                <span className="material-icons font-18">add_circle</span>
                <span>
                  {activeTab === "contacts" ? selectedLanguage?.LstLanguageCodes?.UI_AddContact : selectedLanguage?.LstLanguageCodes?.UI_CreateGroup}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="contacts-content-card">
        {/* Batch Delete Bar */}
        <div className="contacts-batch-delete-bar-wrapper">
          {((activeTab === "contacts" ? selectedContactIds.size : selectedGroupIds.size) > 0) ? (
            <div className="contacts-batch-delete-bar px-3 py-2 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold text-danger font-14">
                  {activeTab === "contacts" ? selectedContactIds.size : selectedGroupIds.size}{" "}
                  {activeTab === "contacts" ? selectedLanguage.LstLanguageCodes.UI_ContactsSelected : selectedLanguage.LstLanguageCodes.UI_GroupsSelected}
                </span>
              </div>
              <button type="button" className="btn btn-danger btn-sm px-3 d-inline-flex align-items-center gap-1 border-r4" onClick={handleRequestBatchDelete}>
                <span className="material-icons font-16">delete</span>
                <span>{selectedLanguage?.LstLanguageCodes?.UI_DeleteSelected}</span>
              </button>
            </div>
          ) : (
            <div className="contacts-batch-delete-bar-placeholder"></div>
          )}
        </div>

        {/* Content Views */}
        {activeTab === "contacts" ? (
          <ContactsList
            contacts={filteredContacts}
            onEdit={handleEditContact}
            onDelete={handleRequestDeleteContact}
            onAddNew={handleOpenAddContact}
            onImport={() => setIsImportModalOpen(true)}
            isReadOnlyScope={isReadOnlyScope}
            selectedIds={selectedContactIds}
            onToggleSelect={handleToggleSelectContact}
            onSelectAll={handleSelectAllContacts}
            isAllSelected={selectableContacts.length > 0 && selectedContactIds.size === selectableContacts.length}
          />
        ) : (
          <ContactGroupsList
            contactGroups={filteredGroups}
            onEdit={handleEditGroup}
            onDelete={handleRequestDeleteGroup}
            onAddNew={handleOpenAddGroup}
            isReadOnlyScope={isReadOnlyScope}
            selectedIds={selectedGroupIds}
            onToggleSelect={handleToggleSelectGroup}
            onSelectAll={handleSelectAllGroups}
            isAllSelected={selectableGroups.length > 0 && selectedGroupIds.size === selectableGroups.length}
          />
        )}
      </div>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => {
            setIsContactModalOpen(false);
            setEditingContact(null);
          }}
          onSave={handleSaveContact}
          initialContact={editingContact}
          existingContacts={contacts}
        />
      )}

      {/* Contact Group Modal */}
      {isGroupModalOpen && (
        <ContactGroupModal
          isOpen={isGroupModalOpen}
          onClose={() => {
            setIsGroupModalOpen(false);
            setEditingGroup(null);
          }}
          onSave={handleSaveGroup}
          initialGroup={editingGroup}
          contacts={contacts}
          contactGroups={contactGroups}
          suggestedDomains={allSuggestedDomains}
          onDomainsImport={handleDomainsImport}
          onContactsImport={handleContactsImportFromGroup}
        />
      )}

      {/* Import Contacts Modal */}
      {isImportModalOpen && (
        <ImportContactsModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImportContacts}
          existingContacts={contacts}
          onOpenValidationPopup={(data) => {
            setValidationData(data);
            setIsValidationPopup(true);
          }}
          showAlert={showAlert}
        />
      )}

      {/* Import Groups Modal */}
      {isImportGroupsModalOpen && (
        <ImportGroupsModal
          isOpen={isImportGroupsModalOpen}
          onClose={() => setIsImportGroupsModalOpen(false)}
          onImport={handleImportGroups}
          existingGroups={contactGroups}
          onOpenValidationPopup={(data) => {
            setValidationData(data);
            setIsValidationPopup(true);
          }}
          showAlert={showAlert}
        />
      )}

      {/* Validation Mistakes Popup */}
      {isValidationPopup && (
        <ValidationPopup
          isValidationPopup={isValidationPopup}
          handleCloseValidationModal={() => setIsValidationPopup(false)}
          selectedLanguage={selectedLanguage}
          duplicateInFile={validationData.duplicateInFile || []}
          emailLeanghtValidation={validationData.emailLeanghtValidation || []}
          invalidEmailsWithoutDotCom={validationData.invalidEmailsWithoutDotCom || []}
          localpartValiadation={validationData.localpartValiadation || []}
          longDomainEmails={validationData.longDomainEmails || []}
          longFirstName={validationData.longFirstName || []}
          longLastName={validationData.longLastName || []}
          invalidMobileNumbers={validationData.invalidMobileNumbers || []}
          unauthorizedCountryCodes={validationData.unauthorizedCountryCodes || []}
          duplicateMobileNumbers={validationData.duplicateMobileNumbers || []}
          missingMobileNumbers={validationData.missingMobileNumbers || []}
          missingCountryCodes={validationData.missingCountryCodes || []}
          longReference={validationData.longReference || []}
          longDescription={validationData.longDescription || []}
          invalidDomain={validationData.invalidDomain || []}
          missingRequiredFields={validationData.missingRequiredFields || []}
          inValidAuthType={[]}
          inValidMsgForBoth=""
          invalidCanPrintValues={[]}
          exceedMaxPrintsPerDoc={[]}
          maxPrintsWithoutCanPrint={[]}
          canPrintNotEnabledAtDocLevel={[]}
          maxPrintsExceedLimit={[]}
          invalidMaxPrintsValues={[]}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modalCus contacts-modal-show">
          <div className="modal-dialog modal-dialog-centered contacts-modal-dialog-md">
            <div className="modal-content border-r4">
              <div className="modal-header d-flex align-items-center justify-content-between">
                <h4 className="mb-0 text-danger fw-bold font-20">
                  {selectedLanguage?.LstLanguageCodes?.UI_Update}
                </h4>
                <button type="button" className="btn btn-outline-danger Readerclose modalclose download" onClick={() => setDeleteConfirm(null)}>
                  <span className="material-icons font-20">highlight_off</span>
                </button>
              </div>
              <div className="modal-body text-center py-4">
                <p className="font-16 text-dark mb-0">
                  {deleteConfirm.type === "contact" ? (
                    <>
                      {selectedLanguage?.LstLanguageCodes?.UI_AreYouSureYouWantToDelete} {deleteConfirm.item?.email} {" "}?
                    </>
                  ) : deleteConfirm.type === "group" ? (
                    <>
                      {selectedLanguage.LstLanguageCodes.UI_SureYouWantToDeleteGroup}{" "}
                      <strong>"{deleteConfirm.item?.name}"</strong>
                    </>
                  ) : deleteConfirm.type === "batchContacts" ? (
                    <>
                      {selectedLanguage.LstLanguageCodes.UI_AreYouSureYouWantToDelete} <strong>{deleteConfirm.count}</strong> {selectedLanguage.LstLanguageCodes.UI_OnceDeletedPermanentlyRemoved}
                    </>
                  ) : (
                    <>
                      {selectedLanguage.LstLanguageCodes.UI_AreYouSureYouWantToDelete} <strong>{deleteConfirm.count}</strong> {selectedLanguage.LstLanguageCodes.UI_SelectedGroupsMemberContactsWillNotBeDeleted}
                    </>
                  )}
                </p>
              </div>
              <div className="modal-footer d-flex justify-content-center gap-3 border-top-0 pt-0 pb-4">
                <button type="button" className="btn btn-danger px-4 me-1" onClick={() => setDeleteConfirm(null)}>
                  {selectedLanguage.LstLanguageCodes.UI_Cancel}
                </button>
                <button type="button" className="btn btn-primary px-4 ms-3" onClick={handleConfirmDelete}>
                  {selectedLanguage.LstLanguageCodes.UI_Confirm}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactsContainer;